import {
  isCredentialVerificationCompleted,
  isCredentialVerificationInProgress,
} from '@terra/shared-utils';

type Credential = {
  id: string;
  verification?: { status?: string | null } | null;
};

export type VerificationStatusById = Record<string, string | null | undefined>;

const isCompleted = (credential: Credential) =>
  isCredentialVerificationCompleted({
    status: credential.verification?.status,
  });

export const collectTransitionedCompletions = ({
  credentials,
  previousStatusById,
}: {
  credentials: readonly Credential[];
  previousStatusById: VerificationStatusById;
}): {
  completedCredentialIds: string[];
  statusById: VerificationStatusById;
} => ({
  completedCredentialIds: credentials
    .filter(
      (credential) =>
        isCompleted(credential) &&
        isCredentialVerificationInProgress({
          status: previousStatusById[credential.id],
        }),
    )
    .map((credential) => credential.id),
  statusById: {
    ...previousStatusById,
    ...Object.fromEntries(
      credentials.map((credential) => [
        credential.id,
        credential.verification?.status,
      ]),
    ),
  },
});

const resolveIdsToClear = ({
  credentials,
  completedCredentialIds,
  isTracked,
}: {
  credentials: readonly Credential[];
  completedCredentialIds: readonly string[];
  isTracked: (credentialId: string) => boolean;
}): string[] =>
  completedCredentialIds.length > 0
    ? completedCredentialIds.filter(isTracked)
    : credentials
        .filter(
          (credential) => isTracked(credential.id) && isCompleted(credential),
        )
        .map((credential) => credential.id);

export const pruneTestingCredentialIds = ({
  testingCredentialIds,
  credentials,
  completedCredentialIds,
}: {
  testingCredentialIds: Set<string>;
  credentials: readonly Credential[];
  completedCredentialIds: readonly string[];
}): Set<string> => {
  const idsToClear = resolveIdsToClear({
    credentials,
    completedCredentialIds,
    isTracked: (credentialId) => testingCredentialIds.has(credentialId),
  });

  if (idsToClear.length === 0) {
    return testingCredentialIds;
  }

  return new Set(
    [...testingCredentialIds].filter(
      (credentialId) => !idsToClear.includes(credentialId),
    ),
  );
};

export const pruneStreamingIds = ({
  streamingIdByCredentialId,
  credentials,
  completedCredentialIds,
}: {
  streamingIdByCredentialId: Record<string, string>;
  credentials: readonly Credential[];
  completedCredentialIds: readonly string[];
}): Record<string, string> => {
  const idsToClear = resolveIdsToClear({
    credentials,
    completedCredentialIds,
    isTracked: (credentialId) =>
      Boolean(streamingIdByCredentialId[credentialId]),
  });

  if (idsToClear.length === 0) {
    return streamingIdByCredentialId;
  }

  return Object.fromEntries(
    Object.entries(streamingIdByCredentialId).filter(
      ([credentialId]) => !idsToClear.includes(credentialId),
    ),
  );
};

export const shouldCloseOpenStream = ({
  openStreamCredentialId,
  credentials,
  completedCredentialIds,
}: {
  openStreamCredentialId: string;
  credentials: readonly Credential[];
  completedCredentialIds: readonly string[];
}): boolean => {
  if (completedCredentialIds.length > 0) {
    return completedCredentialIds.includes(openStreamCredentialId);
  }

  const openCredential = credentials.find(
    (credential) => credential.id === openStreamCredentialId,
  );

  return openCredential !== undefined && isCompleted(openCredential);
};

export const withoutKey = (
  record: Record<string, string>,
  key: string,
): Record<string, string> =>
  record[key]
    ? Object.fromEntries(
        Object.entries(record).filter(([entryKey]) => entryKey !== key),
      )
    : record;

export const resolveVerificationStreamIdForDisplay = ({
  streamingId,
  hasReceivedBrowserFrame,
}: {
  streamingId?: string;
  hasReceivedBrowserFrame: boolean;
}): string | undefined =>
  streamingId && hasReceivedBrowserFrame ? streamingId : undefined;

export const pruneLiveStreamCredentialIds = ({
  liveStreamCredentialIds,
  credentials,
  completedCredentialIds,
}: {
  liveStreamCredentialIds: Set<string>;
  credentials: readonly Credential[];
  completedCredentialIds: readonly string[];
}): Set<string> => {
  const idsToClear = resolveIdsToClear({
    credentials,
    completedCredentialIds,
    isTracked: (credentialId) => liveStreamCredentialIds.has(credentialId),
  });

  if (idsToClear.length === 0) {
    return liveStreamCredentialIds;
  }

  return new Set(
    [...liveStreamCredentialIds].filter(
      (credentialId) => !idsToClear.includes(credentialId),
    ),
  );
};

export const withoutLiveStreamCredentials = ({
  liveStreamCredentialIds,
  credentialIds,
}: {
  liveStreamCredentialIds: Set<string>;
  credentialIds: readonly string[];
}): Set<string> => {
  if (
    credentialIds.length === 0 ||
    credentialIds.every(
      (credentialId) => !liveStreamCredentialIds.has(credentialId),
    )
  ) {
    return liveStreamCredentialIds;
  }

  const idsToClear = new Set(credentialIds);

  return new Set(
    [...liveStreamCredentialIds].filter(
      (credentialId) => !idsToClear.has(credentialId),
    ),
  );
};
